import type { Vec2 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { octahedronSolid, planPoint, prismSolid, pyramidOverRing, ringPoints } from '../../solids';

/**
 * A slender roof turret (dakruiter) on a ridge: a lead-clad octagonal lantern rising from its foot,
 * sunk in the roof, under a needle and a ball finial, as the 1544 woodcut draws one on the town hall.
 */
export const TURRET = { radius: 0.6, segments: 8, lantern: 2.8, finialRise: 0.15, finial: 0.25 } as const;

export interface TurretSpec {
  readonly center: Vec2;
  readonly footY: number;
  readonly topY: number;
}

export interface TurretStyles {
  readonly lantern: VertexStyle;
  readonly needle: VertexStyle;
  readonly finial: VertexStyle;
}

/* Flat faces toward the ridge and the eaves, as on the octagonal lanterns of the churches. */
const PHASE = Math.PI / TURRET.segments;

export function ridgeTurret(builder: MeshBuilder, styles: TurretStyles, frame: Frame, spec: TurretSpec): void {
  const ring = ringPoints(spec.center, TURRET.radius, TURRET.segments, PHASE);
  const lanternTop = spec.footY + TURRET.lantern;
  prismSolid(builder, styles.lantern, frame, ring, spec.footY, lanternTop, { top: false, bottom: false });
  pyramidOverRing(builder, { roof: styles.needle, wall: styles.needle }, frame, ring, lanternTop, { plan: spec.center, y: spec.topY });
  const ball = planPoint(spec.center[0], spec.topY + TURRET.finialRise, spec.center[1]);
  octahedronSolid(builder, styles.finial, frame, ball, TURRET.finial);
}
